import { useEffect, useMemo, useState, type FC, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useQueryClient } from '@tanstack/react-query';
import { InlineNotification, TextInput } from '@carbon/react';
import {
  FullPageWizardLayout,
  useWizardStep,
  type StepConfig,
} from 'components/full-page-wizard';
import type { CrmTractTableRow } from 'pages/crm-tracts-subtracts/crm-tracts-content';
import {
  readWriteFailure,
  UNEXPLAINED_FAILURE,
} from 'pages/well-workspaces/utils';
import routes from '~/routes';
import { keys, postWorkspace, useCrmTractRow, workspacePath } from '../api';
import {
  EMPTY_FOUND,
  EMPTY_TRACT_SEARCH,
  TractFinder,
  TractsReview,
  withRows,
  type FoundTracts,
} from '../pickers';
import styles from './page.module.scss';

const Step: FC<{
  nextEnabled?: boolean;
  onNext?: () => Promise<void>;
  className?: string;
  children: ReactNode;
}> = ({ nextEnabled = true, onNext, className, children }) => {
  const { setNextEnabled, setBackEnabled, setOnNext } = useWizardStep();
  useEffect(() => setNextEnabled(nextEnabled), [nextEnabled, setNextEnabled]);
  useEffect(() => setBackEnabled(true), [setBackEnabled]);
  useEffect(() => {
    if (onNext) setOnNext(onNext);
  }, [onNext, setOnNext]);
  return <div className={classNames(styles.step, className)}>{children}</div>;
};

const ReviewStep: FC<{ name: string; found: FoundTracts }> = ({
  name,
  found,
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { setNavigationGuard } = useWizardStep();
  const [error, setError] = useState<unknown>(null);
  const create = async () => {
    setError(null);
    try {
      const workspace = await postWorkspace({
        name: name.trim(),
        tractIds: found.ids,
        // Only documents a picked tract came through: the rest may vest none of them.
        vestingDocumentIds: [...new Set([...found.via.values()].flat())],
      });
      queryClient.invalidateQueries({ queryKey: keys.all });
      // finishWizard's sequence, plus the router state the show page's toast reads.
      flushSync(() => setNavigationGuard(false));
      // Replaced, so Back from the new workspace skips the finished wizard.
      history.replace(workspacePath(workspace.id), { created: true });
    } catch (failure) {
      setError(failure);
      throw failure;
    }
  };

  return (
    <Step onNext={create} className={styles.review}>
      <h2 className={styles.reviewTitle}>Review: {name.trim()}</h2>
      {error ? (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={
            readWriteFailure(
              error,
              `Could not create the workspace. ${UNEXPLAINED_FAILURE}`
            ) ?? undefined
          }
          onCloseButtonClick={() => setError(null)}
        />
      ) : null}
      <TractsReview found={found} />
    </Step>
  );
};

// Names the seeded tract, and lists it on Review, until its row loads.
const placeholderRow = (id: string, name: string): CrmTractTableRow => ({
  id,
  name,
  parentTractId: null,
  isSubtract: false,
  hasSubtracts: false,
  aliases: [],
  entityName: null,
  entities: [],
  accountName: null,
  accountHref: null,
  surveyArea: null,
  quarterCall: null,
  netAcres: null,
  netAcreageUnits: null,
  grossAcres: null,
  depthRange: null,
  instrumentType: null,
  createdAt: '',
  updatedAt: '',
  vestingDocument: null,
  externalTitleSupportCount: null,
  targetNpvIncluded: null,
  targetNpvHref: null,
});

// Opened from a tract page: that tract is picked, and searched for so its row names it.
const seededFound = (search: string): FoundTracts => {
  const query = new URLSearchParams(search);
  const id = query.get('tract_id');
  const name = query.get('tract_name');
  if (!id || !name) return EMPTY_FOUND;
  return {
    ...EMPTY_FOUND,
    ids: [id],
    rows: new Map([[id, placeholderRow(id, name)]]),
    search: {
      ...EMPTY_TRACT_SEARCH,
      params: { ...EMPTY_TRACT_SEARCH.params, text: name },
    },
  };
};

export const NewTitleSupportWorkspacePage: FC = () => {
  const history = useHistory();
  const { search } = useLocation();
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  // Held here, not in the step: Back and Next remount it.
  const [found, setFound] = useState<FoundTracts>(() => seededFound(search));
  const nameMissing = name.trim() === '';
  // The seeded tract's own row replaces its placeholder: the name search may never return it.
  const { data: seededRow } = useCrmTractRow(
    new URLSearchParams(search).get('tract_id')
  );
  const shown = useMemo(
    () => (seededRow ? withRows(found, [seededRow]) : found),
    [found, seededRow]
  );

  const steps: StepConfig[] = [
    {
      label: 'Name and Tracts',
      content: (
        <Step
          nextEnabled={!nameMissing && found.ids.length > 0}
          className={styles.tracts}
        >
          <div className={styles.nameField}>
            <TextInput
              id="title-support-workspace-name"
              labelText="Name *"
              placeholder="Enter a workspace name"
              required
              value={name}
              invalid={nameTouched && nameMissing}
              invalidText="Name is required"
              onBlur={() => setNameTouched(true)}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className={styles.tableWrapper}>
            <TractFinder found={shown} onChange={setFound} />
          </div>
        </Step>
      ),
    },
    {
      label: 'Review',
      nextLabel: 'Create',
      content: <ReviewStep name={name} found={shown} />,
    },
  ];

  return (
    <FullPageWizardLayout
      breadcrumbs={[
        [routes.titleSupportWorkspacesPath, 'Title Support Workspaces'],
      ]}
      title="New Title Support Workspace"
      steps={steps}
      onCancel={() => history.push(routes.titleSupportWorkspacesPath)}
    />
  );
};
